// 東アジア・東南アジア・南アジアの世界史スポット。
// 中華帝国の統合、シルクロードの東西交流、インド世界の宗教、東南アジアの海域交易を
// 代表させる。レコードの各フィールドの意味は src/historicSitesData.js の冒頭を参照。

export const ASIA_SITES = [
  // ---------------- 東アジア ----------------
  {
    id: 'great-wall',
    name: '万里の長城（八達嶺）',
    nameEn: 'Great Wall of China (Badaling)',
    lat: 40.3592, lng: 116.0169, elevationM: 800,
    region: 'asia', era: 'medieval',
    country: '中国（北京市）',
    civilization: '春秋戦国〜秦〜明',
    yearLabel: '前214年頃 秦が連結 / 現存部の多くは明代（14〜17世紀）',
    whcYear: 1987,
    emoji: '🧱',
    summary:
      '春秋戦国時代に趙・燕・秦などが北方の遊牧民に備えて築いた城壁を、前214年頃に秦の始皇帝が連結・修築したのが起源とされる。以後、漢・北魏・北斉などが延伸し、匈奴・突厥・モンゴルといった草原の勢力と、農耕を営む中華世界とを分ける境界線として機能し続けた。ただし観光地として知られる磚（せん）積みの堅固な城壁の大半は、モンゴル（北元）の脅威に備えた明代の再建である。支線を含めた総延長は2万kmを超えるとされ、遊牧と農耕の対立という東アジア史の基調を、地形そのものに刻み込んでいる。',
    quiz: {
      statement: '現存する万里の長城の大部分は、秦の始皇帝が築いた当時のものである。',
      answer: false,
      explain: '始皇帝は各国の長城を連結・修築しましたが、それは版築（土を突き固めたもの）が中心でした。現在見られる磚積みの城壁の多くは明代の再建です。',
    },
  },
  {
    id: 'terracotta-army',
    name: '秦始皇帝陵と兵馬俑坑',
    nameEn: 'Terracotta Army',
    lat: 34.3845, lng: 109.2785, elevationM: 470,
    region: 'asia', era: 'ancient',
    country: '中国（陝西省西安）',
    civilization: '秦',
    yearLabel: '前3世紀',
    whcYear: 1987,
    emoji: '🗿',
    facts: [
      { label: '発見', value: '1974年、井戸掘りの最中に偶然' },
      { label: '規模', value: 'これまでに約8,000体を確認' },
    ],
    summary:
      '中国を統一した秦の始皇帝の陵墓の東約1.5kmに埋められた、等身大の陶製兵士と軍馬の軍団。1974年に井戸を掘っていた農民が偶然発見し、これまでに約8千体が確認されている。一体ごとに顔つきや髪型が異なり、実在の軍団編成を写したとみられる。始皇帝は郡県制を敷き、度量衡・貨幣・文字を統一して中央集権化を進める一方、死後の世界にも自らの軍を従えようとした。陵墓の本体は未発掘のままで、水銀の川をめぐらせたという『史記』の記述をどう検証するかが今も課題である。',
    quiz: {
      statement: '兵馬俑は始皇帝陵の墳丘内部から発見された。',
      answer: false,
      explain: '兵馬俑坑は陵墓の本体ではなく、その東約1.5kmに位置する陪葬坑です。始皇帝陵の墳丘内部は保存技術の問題もあり、現在も発掘されていません。',
    },
  },
  {
    id: 'forbidden-city',
    name: '紫禁城（故宮）',
    nameEn: 'Forbidden City',
    lat: 39.9163, lng: 116.3972, elevationM: 45,
    region: 'asia', era: 'earlymodern',
    country: '中国（北京市）',
    civilization: '明・清',
    yearLabel: '1406〜1420年 造営',
    whcYear: 1987,
    emoji: '🏯',
    facts: [
      { label: '規模', value: '南北 約961m × 東西 約753m' },
    ],
    summary:
      '明の第3代永楽帝が南京から北京へ遷都するにあたり、1406年から約15年をかけて造営した宮殿。南北約961m、東西約753mの城壁に囲まれ、部屋数は9千室近い。中心軸上に太和殿・中和殿・保和殿を並べる配置は、天と皇帝を結ぶ儒教的な宇宙観を地上に写したものである。明・清あわせて24人の皇帝がここに住み、1912年の宣統帝退位で王朝時代は幕を閉じた。永楽帝は鄭和に南海遠征を命じた皇帝でもあり、この宮殿は明が内に権威を固め、外へ勢力を広げた時代の産物といえる。',
    quiz: {
      statement: '紫禁城を造営したのは、都を南京から北京へ移した明の永楽帝である。',
      answer: true,
      explain: '永楽帝は靖難の変で帝位に就いたのち、自らの根拠地であった北京へ遷都し、紫禁城を造営しました。鄭和の南海遠征を命じたのも同じ皇帝です。',
    },
  },
  {
    id: 'mogao-caves',
    name: '莫高窟（敦煌）',
    nameEn: 'Mogao Caves',
    lat: 40.0420, lng: 94.8096, elevationM: 1330,
    region: 'asia', era: 'ancient',
    country: '中国（甘粛省敦煌）',
    civilization: '五胡十六国〜元',
    yearLabel: '366年頃 開削 / 14世紀まで造営',
    whcYear: 1987,
    emoji: '🛕',
    facts: [
      { label: '規模', value: '現存735窟・壁画 約45,000㎡' },
    ],
    summary:
      'シルクロードの要衝である敦煌の郊外、鳴沙山の断崖に366年頃から千年にわたって掘り続けられた仏教石窟群。現存する735窟に、壁画約4万5千平方メートルと塑像2千余体が残る。インドのガンダーラ様式から中国風へと変容していく仏教美術の流れを一望でき、東西文化交流の生きた年表というべき場所である。1900年に道士王円籙が発見した「蔵経洞」からは、漢語・チベット語・ソグド語などで書かれた5万点近い文書が出土し、敦煌学という新しい学問分野を生んだ。',
    quiz: {
      statement: '莫高窟の壁画は、すべて同じ時代に一気に描かれたものである。',
      answer: false,
      explain: '4世紀から14世紀まで、約千年にわたって造営が続きました。時代ごとに様式が異なるため、仏教美術と東西交流の変遷をここから追うことができます。',
    },
  },
  {
    id: 'qufu-confucius',
    name: '曲阜の孔子廟',
    nameEn: 'Temple of Confucius, Qufu',
    lat: 35.5967, lng: 116.9885, elevationM: 60,
    region: 'asia', era: 'ancient',
    country: '中国（山東省曲阜）',
    civilization: '春秋時代（魯）／歴代王朝',
    yearLabel: '前478年 創建 / 現在の建物は明・清期',
    whcYear: 1994,
    emoji: '🛕',
    summary:
      '孔子（前551頃〜前479頃）の故郷である魯の都・曲阜に、その死の翌年、旧居を廟としたのが始まりと伝わる。孔子は諸国が覇を競う春秋の乱世に、仁と礼による秩序の回復を説いた。その思想は前漢の武帝が儒学を官学としたことで国家の統治理念となり、科挙を通じて千数百年にわたり官僚を育てる規範となった。その影響は中国にとどまらず、朝鮮・日本・ベトナムにも及んだ。歴代王朝が競って拡張した現在の建物群は明・清期のもので、孔子の子孫が代々暮らした孔府も隣接する。',
    quiz: {
      statement: '儒学が国家の正統な学問（官学）とされたのは、孔子が生きていた春秋時代のことである。',
      answer: false,
      explain: '孔子は生前、諸国を巡っても用いられませんでした。儒学が官学となったのは、その死から約350年後、前漢の武帝が董仲舒の献策を容れてからです。',
    },
  },
  {
    id: 'bulguksa',
    name: '仏国寺',
    nameEn: 'Bulguksa',
    lat: 35.7900, lng: 129.3322, elevationM: 250,
    region: 'asia', era: 'medieval',
    country: '韓国（慶尚北道慶州）',
    civilization: '統一新羅',
    yearLabel: '751年 造営開始',
    whcYear: 1995,
    whcNote: '「石窟庵と仏国寺」',
    emoji: '🛕',
    summary:
      '新羅の都であった慶州の郊外に、751年から造営が進められた仏教寺院。白雲橋・青雲橋の石段や多宝塔・釈迦塔などの石造建築に、統一新羅の高い技術が残る。7世紀に唐と結んで百済・高句麗を破り朝鮮半島の大半を統一した新羅は、唐の律令や仏教を積極的に取り入れ、遣唐使や留学僧を通じて東アジアの文化圏に深く組み込まれた。同時期に日本で東大寺の大仏が造られていることからも、8世紀の東アジアが仏教を共通言語としていた様子がうかがえる。',
    quiz: {
      statement: '仏国寺が造営された8世紀、朝鮮半島は高句麗・百済・新羅が並び立つ三国時代だった。',
      answer: false,
      explain: '新羅は唐と結んで660年に百済、668年に高句麗を滅ぼし、その後は唐の勢力も退けて半島の大半を統一しました。8世紀は統一新羅の全盛期です。',
    },
  },
  {
    id: 'karakorum',
    name: 'カラコルム（エルデネ・ゾー）',
    nameEn: 'Karakorum / Erdene Zuu',
    lat: 47.2000, lng: 102.8420, elevationM: 1470,
    region: 'asia', era: 'medieval',
    country: 'モンゴル（ウブルハンガイ県）',
    civilization: 'モンゴル帝国',
    yearLabel: '1235年 オゴタイ・ハンが造営 / 1586年 エルデネ・ゾー創建',
    whcYear: 2004,
    whcNote: '「オルホン渓谷文化的景観」の一部',
    emoji: '🐎',
    summary:
      'チンギス・ハンの後を継いだオゴタイ・ハンが1235年にオルホン川流域に築いた、モンゴル帝国の首都。ユーラシアの東西を結ぶ駅伝制（ジャムチ）の中心として、中国の職人、イスラーム商人、ヨーロッパからの使節フランチェスコ会士ルブルックらが集まる国際都市だった。フビライが大都（北京）へ都を移すと衰え、廃墟の石材を使って1586年にチベット仏教寺院エルデネ・ゾーが建てられた。「モンゴルの平和（パクス・モンゴリカ）」が東西交流を一気に加速させた時代の中心である。',
    quiz: {
      statement: 'モンゴル帝国はカラコルムを最後まで首都とし、中国へ都を移すことはなかった。',
      answer: false,
      explain: '第5代フビライは1264年に大都（現在の北京）へ遷都し、国号を元と定めました。カラコルムはこれ以後、急速に衰えていきます。',
    },
  },

  // ---------------- 東南アジア ----------------
  {
    id: 'angkor-wat',
    name: 'アンコール・ワット',
    nameEn: 'Angkor Wat',
    lat: 13.4125, lng: 103.8670, elevationM: 35,
    region: 'asia', era: 'medieval',
    country: 'カンボジア（シェムリアップ）',
    civilization: 'クメール王朝（アンコール朝）',
    yearLabel: '12世紀前半（スーリヤヴァルマン2世）',
    whcYear: 1992,
    emoji: '🛕',
    summary:
      'クメール王朝のスーリヤヴァルマン2世が12世紀前半に、ヒンドゥー教の神ヴィシュヌに捧げる寺院として造営した。中央の五つの尖塔は神々の住む須弥山（メール山）を、周囲をめぐる環濠は大海を表し、伽藍全体がヒンドゥー的な宇宙の縮図となっている。回廊の壁面には「乳海攪拌」の神話や王の行軍が延々と浮彫にされ、当時の王権が神話と結びついていたことを物語る。のちに上座部仏教の寺院へ転用され、今日ではカンボジア国旗に描かれる国の象徴となっている。',
    quiz: {
      statement: 'アンコール・ワットは、建立された当初から仏教寺院として造られた。',
      answer: false,
      explain: '当初はヒンドゥー教のヴィシュヌ神に捧げられた寺院でした。16世紀頃までに上座部仏教の寺院へと転用され、現在に至ります。',
    },
  },
  {
    id: 'borobudur',
    name: 'ボロブドゥール',
    nameEn: 'Borobudur',
    lat: -7.6079, lng: 110.2038, elevationM: 265,
    region: 'asia', era: 'medieval',
    country: 'インドネシア（ジャワ島中部）',
    civilization: 'シャイレンドラ朝',
    yearLabel: '8世紀後半〜9世紀前半',
    whcYear: 1991,
    emoji: '🛕',
    facts: [
      { label: '浮彫', value: '約1,460面' },
    ],
    summary:
      'ジャワ島中部にシャイレンドラ朝が築いた、世界最大級の仏教遺跡。方形の基壇から円壇まで九層に積み上げられた石造建築で、内部に礼拝空間をもたない巨大なストゥーパである。方形の回廊には仏伝やジャータカなど約1460面の浮彫が並び、下から上へと巡ることで欲界・色界・無色界という悟りへの階梯をたどる構成になっている。のち火山灰と密林に埋もれたが、1814年にイギリス副総督ラッフルズの命による調査で再発見された。海の道を通じた仏教の伝播を示す記念碑である。',
    quiz: {
      statement: 'ボロブドゥールは、内部に広い礼拝空間をもつ寺院建築である。',
      answer: false,
      explain: '内部空間をもたない石積みの大ストゥーパで、参拝者は外側の回廊を下から上へ巡りながら、浮彫をたどって悟りへの道を象徴的に歩みます。',
    },
  },
  {
    id: 'bagan',
    name: 'バガン（アーナンダ寺院）',
    nameEn: 'Bagan (Ananda Temple)',
    lat: 21.1702, lng: 94.8619, elevationM: 65,
    region: 'asia', era: 'medieval',
    country: 'ミャンマー（マンダレー地方域）',
    civilization: 'パガン朝',
    yearLabel: '11〜13世紀（アーナンダ寺院は1105年頃）',
    whcYear: 2019,
    emoji: '🛕',
    summary:
      'エーヤワディー川中流の平原に、11世紀から13世紀にかけて数千の仏塔と寺院が建てられた。ビルマ人最初の統一王朝パガン朝のアノーヤター王がスリランカから上座部仏教を受け入れ、以後の歴代王が競って寄進を重ねた結果である。アーナンダ寺院は1105年頃の建立で、インド的な要素とビルマ独自の意匠が融合した代表作とされる。13世紀末にモンゴル（元）の圧力を受けて王朝は衰えたが、この地に根づいた上座部仏教は、現在のミャンマー社会の基層をなしている。',
    quiz: {
      statement: 'パガン朝はスリランカから上座部仏教を受け入れ、それが今日のミャンマーの信仰の基礎となった。',
      answer: true,
      explain: 'アノーヤター王が上座部仏教を国の宗教として受け入れ、バガンに大量の仏塔が築かれました。上座部仏教はタイやカンボジアにも広がっていきます。',
    },
  },
  {
    id: 'ayutthaya',
    name: 'アユタヤ',
    nameEn: 'Ayutthaya',
    lat: 14.3556, lng: 100.5583, elevationM: 5,
    region: 'asia', era: 'earlymodern',
    country: 'タイ（アユタヤ県）',
    civilization: 'アユタヤ朝',
    yearLabel: '1351〜1767年',
    whcYear: 1991,
    emoji: '🛕',
    summary:
      'チャオプラヤー川の中州に築かれ、400年余りにわたってアユタヤ朝の都として栄えた国際商業都市。中国・インド・ペルシア・そしてポルトガルやオランダの商人が集まり、17世紀には日本町も形成されて山田長政が国王に仕えたと伝えられる。米と森林産物の輸出で富を蓄え、仏塔や寺院が林立したが、1767年にビルマのコンバウン朝の侵攻を受けて徹底的に破壊された。都はトンブリー、続いてバンコクへ移る。菩提樹の根に取り込まれた仏頭が、その断絶を静かに伝えている。',
    quiz: {
      statement: '17世紀のアユタヤには日本町があり、日本人が国王に仕えることもあった。',
      answer: true,
      explain: '朱印船貿易で渡った日本人が日本町を形成し、山田長政は国王に仕えて高い官位を得たと伝えられます。江戸幕府の鎖国政策の後、日本町は衰えました。',
    },
  },
  {
    id: 'malacca',
    name: 'マラッカ',
    nameEn: 'Malacca',
    lat: 2.1917, lng: 102.2500, elevationM: 25,
    region: 'asia', era: 'earlymodern',
    country: 'マレーシア（マラッカ州）',
    civilization: 'マラッカ王国／ポルトガル・オランダ植民地',
    yearLabel: '15世紀初 建国 / 1511年 ポルトガルが占領',
    whcYear: 2008,
    whcNote: '「マラッカ海峡の古都群」の一部',
    emoji: '⛵',
    summary:
      'マレー半島南西岸に15世紀初めに興った港市国家。明の鄭和の南海遠征をきっかけに中国と結び、やがてイスラームを受け入れて、香辛料交易の結節点として東南アジア随一の繁栄を築いた。マラッカ海峡は今日でも世界有数の海上交通路である。1511年、アルブケルケ率いるポルトガルがここを占領したことは、ヨーロッパ勢力がアジアの海の交易網に本格的に割り込んだ画期となった。のちオランダ、イギリスへと支配者は移り、丘の上には聖ポール教会の廃墟が今も残る。',
    quiz: {
      statement: 'ポルトガルは1511年にマラッカを占領し、アジアの香辛料交易に本格的に進出した。',
      answer: true,
      explain: 'アルブケルケの遠征によるマラッカ占領は、ゴア（1510年）に続く拠点獲得で、ヨーロッパ勢力がアジア海域の交易網へ食い込む転機となりました。',
    },
  },

  // ---------------- 南アジア ----------------
  {
    id: 'mohenjo-daro',
    name: 'モヘンジョ・ダロ',
    nameEn: 'Mohenjo-daro',
    lat: 27.3294, lng: 68.1386, elevationM: 50,
    region: 'asia', era: 'prehistory',
    country: 'パキスタン（シンド州）',
    civilization: 'インダス文明',
    yearLabel: '前2500年頃〜前1800年頃',
    whcYear: 1980,
    emoji: '🏛',
    summary:
      'インダス川下流域に栄えた計画都市で、1922年に発見された。焼煉瓦を用いた碁盤目状の街路、各戸の井戸と浴室、街路の下を走る排水溝など、前2500年頃としては驚くべき都市衛生設備を備えている。「大浴場」や穀物倉とされる建物はあるが、エジプトやメソポタミアのような巨大な王墓・神殿が見当たらないことから、強大な王権よりも交易と祭祀を核とする社会だったとする説が有力である。印章に刻まれたインダス文字は今も未解読で、衰退の原因についても定説をみない。',
    quiz: {
      statement: 'インダス文明で使われた文字は、すでに解読されている。',
      answer: false,
      explain: 'インダス文字は印章などに約400種の記号が残りますが未解読です。ロゼッタ・ストーンで解読されたエジプトの神聖文字などとは対照的です。',
    },
  },
  {
    id: 'sanchi',
    name: 'サーンチーの仏塔',
    nameEn: 'Great Stupa of Sanchi',
    lat: 23.4794, lng: 77.7392, elevationM: 450,
    region: 'asia', era: 'ancient',
    country: 'インド（マディヤ・プラデーシュ州）',
    civilization: 'マウリヤ朝〜',
    yearLabel: '前3世紀 創建（アショーカ王）',
    whcYear: 1989,
    emoji: '🛕',
    summary:
      'マウリヤ朝のアショーカ王が前3世紀に創建したストゥーパ（仏塔）を核とする仏教遺跡群。カリンガ征服の惨状を悔いて仏教に帰依したアショーカ王は、ダルマ（法）による統治を掲げて各地に石柱と仏塔を建て、スリランカなど国外へも布教の使節を送った。第1塔を囲む塔門（トラナ）には、仏伝やジャータカの説話が精緻に彫られている。ただし初期の仏教美術では釈迦を人の姿で表さず、菩提樹・法輪・仏足石といった象徴で示す点に、当時の信仰のかたちがよく現れている。',
    quiz: {
      statement: 'サーンチーの初期の彫刻では、釈迦（ブッダ）の姿は人間の形では表現されていない。',
      answer: true,
      explain: '初期仏教美術は無仏像時代と呼ばれ、菩提樹・法輪・仏足石などの象徴で釈迦を示しました。人の姿の仏像はクシャーナ朝期のガンダーラとマトゥラーで生まれます。',
    },
  },
  {
    id: 'ajanta',
    name: 'アジャンター石窟群',
    nameEn: 'Ajanta Caves',
    lat: 20.5519, lng: 75.7033, elevationM: 380,
    region: 'asia', era: 'ancient',
    country: 'インド（マハーラーシュトラ州）',
    civilization: 'サータヴァーハナ朝／ヴァーカータカ朝',
    yearLabel: '前2世紀〜後7世紀頃',
    whcYear: 1983,
    emoji: '🛕',
    summary:
      'ワゴーラー渓谷の断崖に、前2世紀頃から後7世紀頃にかけて掘られた30の仏教石窟。前期は無仏像時代の簡素な窟、後期はグプタ朝の影響を受けた華麗な壁画と仏像で飾られる。とくに第1窟の蓮華手菩薩の壁画は、しなやかな身体表現と豊かな色彩で知られ、その様式は中央アジアを経て敦煌、さらに日本の法隆寺金堂壁画にまで影響を与えたといわれる。密林に埋もれていたが、1819年にイギリス軍人が虎狩りの最中に偶然発見した。',
    quiz: {
      statement: 'アジャンター石窟の壁画様式は、シルクロードを経て東アジアの仏教美術にも影響を与えたとされる。',
      answer: true,
      explain: 'グプタ様式の造形は中央アジアの石窟を経て中国へ伝わりました。法隆寺金堂壁画との類似がしばしば指摘されるのも、この長い伝播経路によるものです。',
    },
  },
  {
    id: 'qutb-minar',
    name: 'クトゥブ・ミナール',
    nameEn: 'Qutb Minar',
    lat: 28.5245, lng: 77.1855, elevationM: 215,
    region: 'asia', era: 'medieval',
    country: 'インド（デリー）',
    civilization: 'デリー・スルタン朝（奴隷王朝）',
    yearLabel: '1199年頃 起工',
    whcYear: 1993,
    emoji: '🕌',
    facts: [
      { label: '高さ', value: '約72.5m' },
    ],
    summary:
      '北インドに最初のイスラーム王朝を開いたアイバクが、1199年頃に建て始めた高さ約72.5mの尖塔（ミナレット）。アイバクはゴール朝の将軍からデリーのスルタンとなった人物で、その出自から彼の王朝は奴隷王朝と呼ばれる。塔の隣のモスクは、破壊されたヒンドゥー教・ジャイナ教寺院の石材を転用して建てられており、柱には元のヒンドゥー的な彫刻が今も残る。イスラームが北インドに定着し、やがてムガル帝国へとつながる長い共存と衝突の始まりを示す遺構である。',
    quiz: {
      statement: 'クトゥブ・ミナールを建てたアイバクは、北インドで最初のイスラーム王朝を開いた。',
      answer: true,
      explain: 'アイバクはゴール朝の将軍からデリーのスルタンとなり、奴隷王朝を開きました。以後デリーでは5つのイスラーム王朝が交替し、デリー・スルタン朝と総称されます。',
    },
  },
  {
    id: 'taj-mahal',
    name: 'タージ・マハル',
    nameEn: 'Taj Mahal',
    lat: 27.1751, lng: 78.0421, elevationM: 170,
    region: 'asia', era: 'earlymodern',
    country: 'インド（アーグラ）',
    civilization: 'ムガル帝国',
    yearLabel: '1632年 着工 / 1653年頃 完成',
    whcYear: 1983,
    emoji: '🕌',
    summary:
      'ムガル帝国第5代皇帝シャー・ジャハーンが、出産で亡くなった愛妃ムムターズ・マハルのために建てた総大理石の廟。1632年に着工し、約2万人を動員して20年余りで完成した。中央のドームと四隅のミナレット、左右対称の庭園はペルシア・イスラーム建築の系譜を引き、白大理石に色石を象嵌するピエトラ・ドゥーラの技法にはインド在来の工芸が生きている。ヒンドゥーとイスラームの様式が交わったインド・イスラーム文化の到達点とされ、のちに皇帝自身もここに葬られた。',
    quiz: {
      statement: 'タージ・マハルは、ムガル帝国の皇帝が自らのために建てた宮殿である。',
      answer: false,
      explain: '宮殿ではなく廟（墓廟）です。シャー・ジャハーンが亡き妃ムムターズ・マハルのために建てたもので、のちに皇帝自身もここに葬られました。',
    },
  },
  {
    // 出典: https://royal.khs.go.kr/ENG/contents/E101010000.do
    sources: ['https://royal.khs.go.kr/ENG/contents/E101010000.do'],
    id: 'gyeongbokgung',
    name: '景福宮', nameEn: 'Gyeongbokgung Palace',
    lat: 37.5796, lng: 126.9770, elevationM: 45,
    region: 'asia', era: 'medieval',
    country: '韓国（ソウル）', civilization: '朝鮮王朝',
    yearLabel: '1395年 創建 / 1867年 再建',
    whcYear: null, emoji: '🏯',
    summary:
      '朝鮮王朝の成立後、首都を漢陽へ移した太祖の時代に1395年に建てられた正宮。漢陽は現在のソウルにあたる。正門から主要な殿舎が軸に沿って並ぶ配置は、王と臣下の序列や儀礼を重んじる王朝の政治を空間に表している。1592年の日本の侵攻時に焼失し、長く再建されなかったが、1867年に再び宮殿が整えられた。さらに日本の植民地支配下で多くの建物が失われ、20世紀末から復元が進められた。したがって、現在の姿には創建・破壊・再建という複数の時代が重なる。空から山を背にした宮殿と現代の大通りを見比べると、王朝の都の構造がソウルに残ることを読み取れる。文化財の復元が歴史の記憶とどう関わるかも考えられる。',
    quiz: {
      statement: '景福宮の建物は、1395年の創建以来、焼失も再建もせずに残っている。',
      answer: false,
      explain: '1592年の焼失や1867年の再建、その後の破壊と復元を経験しています。現在の景観には複数の時代が重なっています。',
    },
  },
  {
    // 出典: https://whc.unesco.org/en/list/881/
    sources: ['https://whc.unesco.org/en/list/881/'],
    id: 'temple-of-heaven',
    name: '天壇（祈年殿）', nameEn: 'Temple of Heaven',
    lat: 39.8822, lng: 116.4066, elevationM: 45,
    region: 'asia', era: 'earlymodern',
    country: '中国（北京）', civilization: '明・清',
    yearLabel: '1420年 天地壇完成 / 明・清代に改修',
    whcYear: 1998, emoji: '🛕',
    summary:
      '明の永楽帝の時代、1420年に北京の南部に造られた皇帝の祭祀施設。明・清の皇帝はここで天をまつり、豊作を祈った。紫禁城が日常の政治や居住の中心だったのに対し、天壇は皇帝が天と人々を結ぶ存在であることを儀礼で示す場所だった。円形の祈年殿や祭壇、南北の軸に沿った配置には、天地や数に関する考え方が反映されている。施設は何度も改修され、現在の構成は一度の工事で完成したものではない。上空から円い建築と周囲の方形の区画を眺め、北にある紫禁城との位置関係を確かめると、都市全体が政治と宇宙観を表す舞台だったことを考えられる。建築の形に込められた意味を探す楽しさがある。',
    quiz: {
      statement: '天壇は、皇帝がふだん寝起きするための居住用宮殿として造られた。',
      answer: false,
      explain: '天壇は天をまつり、豊作などを祈る祭祀の場でした。政治や居住の中心である紫禁城とは役割が異なります。',
    },
  },
  {
    // 出典: https://whc.unesco.org/en/list/1483/
    sources: ['https://whc.unesco.org/en/list/1483/'],
    id: 'singapore-botanic-gardens',
    name: 'シンガポール植物園', nameEn: 'Singapore Botanic Gardens',
    lat: 1.3148, lng: 103.8152, elevationM: 25,
    region: 'asia', era: 'modern',
    country: 'シンガポール', civilization: 'イギリス植民地時代・熱帯植物学',
    yearLabel: '1859年 開園',
    whcYear: 2015, emoji: '🌳',
    summary:
      '1859年に開園した熱帯植物園で、市民が風景を楽しむ庭から、研究や植物の保全を担う施設へと発展した。特にゴムの栽培研究と苗の普及は、東南アジアでのゴム産業の拡大に大きく関わった。イギリスのキュー植物園などと結ばれた植物の移動は、科学の交流であると同時に、植民地の経済を組み替える動きでもあった。美しい緑の背後に、商品作物や国際貿易の歴史がある点が特徴である。園内には長い運営の過程を伝える樹木や景観、建物が残る。上空から都市の中に広がる緑を見ると、植物園が自然保護だけでなく教育と暮らしにも関わることが分かる。東京の明治神宮の森と比べ、人が育てた緑でも目的や成り立ちが異なることを学べる。',
    quiz: {
      statement: 'シンガポール植物園は、東南アジアのゴム栽培の発展にも関わった。',
      answer: true,
      explain: 'ゴムの研究や苗の普及を通じて地域の産業に影響しました。植物園は観賞だけでなく科学と経済の歴史にもつながります。',
    },
  },
  {
    // 出典: https://en.tshs.ntpc.gov.tw/xmdoc/cont?lang=en&xsmsid=0G292396897604829770
    sources: ['https://en.tshs.ntpc.gov.tw/xmdoc/cont?lang=en&xsmsid=0G292396897604829770'],
    id: 'fort-san-domingo',
    name: '紅毛城（淡水）', nameEn: 'Fort San Domingo',
    lat: 25.1754, lng: 121.4326, elevationM: 30,
    region: 'asia', era: 'earlymodern',
    country: '台湾（新北市淡水区）', civilization: '海上交易・スペインとオランダ',
    yearLabel: '1628年 スペイン人が築城 / 1644年 オランダ人が新砦建設',
    whcYear: null, emoji: '🏰',
    summary:
      '淡水河の河口を見下ろす丘に位置する砦。1628年にスペイン人が築いた城が前身で、オランダ人が進出した後、1644年に新たな砦が建設された。現在見られる主な城の起源はこのオランダ時代にあり、英語名がスペイン時代の名を受け継ぐことと区別して考える必要がある。19世紀にはイギリス領事館として使われ、異なる勢力が海の交通を求めて関わった歴史が重なる。砦や領事館は軍事・外交・商業が結び付く施設だった。上空から河口と城の高台を見れば、船の出入りを見渡せる立地の意味が分かる。長崎の出島やマラッカとつなげて考えると、東アジアの港が広い海上交易の網の中に位置していたことを学べる。',
    quiz: {
      statement: '現在の紅毛城の主な城は、1628年にスペイン人が建てた木造砦そのものである。',
      answer: false,
      explain: '主な城はオランダ人が1644年に築いた砦に由来します。現在の英語名と、現存する建物の起源には違いがあります。',
    },
  },
];
